import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2, Ticket, Plus, Building2, User as UserIcon } from 'lucide-react';
import { toast } from 'sonner';
import { SellDayPassPackageModal } from './SellDayPassPackageModal';

export const SalesDayPassesTab = ({ api, user, venues = [] }) => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSellModal, setShowSellModal] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/day-pass-packages');
      setPackages(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load your Bulk Day Pass packages');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const venueName = (id) => venues.find((v) => v.id === id)?.name || id;

  const totals = packages.reduce((acc, p) => ({
    purchased: acc.purchased + (p.quantity_purchased || 0),
    used: acc.used + (p.quantity_used || 0),
    remaining: acc.remaining + (p.quantity_remaining || 0),
  }), { purchased: 0, used: 0, remaining: 0 });

  return (
    <div className="space-y-6" data-testid="sales-day-passes-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-heading">Bulk Day Pass Packages</h2>
          <p className="text-sm text-muted-foreground">Bulk Day-Pass packages you've sold, and their usage.</p>
        </div>
        <Button onClick={() => setShowSellModal(true)} data-testid="day-pass-sell-open-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Sell a Package
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Card><CardContent className="p-4">
          <p className="text-xs text-muted-foreground">Total Sold</p>
          <p className="text-2xl font-bold mt-1">{totals.purchased}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-xs text-muted-foreground">Used</p>
          <p className="text-2xl font-bold mt-1 text-orange-600">{totals.used}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-xs text-muted-foreground">Remaining</p>
          <p className="text-2xl font-bold mt-1 text-green-700">{totals.remaining}</p>
        </CardContent></Card>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : packages.length === 0 ? (
        <Card><CardContent className="py-12 text-center">
          <Ticket className="w-12 h-12 mx-auto text-muted-foreground/50 mb-3" />
          <p className="text-muted-foreground">You haven't sold any Bulk Day Pass packages yet.</p>
        </CardContent></Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-left">
                  <tr>
                    <th className="px-4 py-3 font-medium">Client</th>
                    <th className="px-4 py-3 font-medium">Valid At</th>
                    <th className="px-4 py-3 font-medium text-right">Sold</th>
                    <th className="px-4 py-3 font-medium text-right">Used</th>
                    <th className="px-4 py-3 font-medium text-right">Remaining</th>
                    <th className="px-4 py-3 font-medium">Starts</th>
                    <th className="px-4 py-3 font-medium">Expiry</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {packages.map((p) => {
                    const isScheduled = p.status === 'active' && p.start_date && new Date(p.start_date) > new Date();
                    return (
                    <tr key={p.id} className="border-t border-border" data-testid={`day-pass-sold-row-${p.id}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {p.organization_id ? <Building2 className="w-4 h-4 text-primary" /> : <UserIcon className="w-4 h-4 text-muted-foreground" />}
                          <div className="min-w-0">
                            <div className="font-medium truncate">{p.client_name || 'Unknown client'}</div>
                            {p.client_email && <div className="text-xs text-muted-foreground truncate">{p.client_email}</div>}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1 max-w-[180px]">
                          {(p.venue_ids && p.venue_ids.length > 0) ? p.venue_ids.map((vid) => (
                            <Badge key={vid} variant="outline" className="text-xs">{venueName(vid)}</Badge>
                          )) : <span className="text-xs text-muted-foreground">All venues</span>}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right font-mono">{p.quantity_purchased}</td>
                      <td className="px-4 py-3 text-right font-mono text-orange-600">{p.quantity_used}</td>
                      <td className="px-4 py-3 text-right font-mono font-semibold text-green-700">{p.quantity_remaining}</td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">{p.start_date ? new Date(p.start_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}</td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">{new Date(p.expiry_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                      <td className="px-4 py-3">
                        {isScheduled ? (
                          <Badge variant="outline" className="text-xs bg-amber-500/10 text-amber-700 border-amber-500/30">Scheduled</Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs capitalize">{p.status}</Badge>
                        )}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      <SellDayPassPackageModal
        open={showSellModal}
        onClose={() => setShowSellModal(false)}
        api={api}
        onSold={fetchData}
        venues={venues}
      />
    </div>
  );
};

export default SalesDayPassesTab;
